/**
 * F2-a: card "oggi" di un hotel per la dashboard owner.
 *
 * Layout (mobile-first, una card per hotel):
 *  - header: nome hotel + badge stato (● verde Online / ● grigio Offline /
 *    ● grigio "Mai collegato" se lastHeartbeatAt e' null: un hotel mai
 *    attivato non e' "guasto", e' semplicemente mai partito — stato neutro)
 *  - incasso del giorno IN EVIDENZA (numero grande, accento)
 *  - camere occupate/totali con barra occupazione
 *  - ultimo contatto in tempo relativo (da lastHeartbeatAt)
 *
 * Usa i token Tailwind del design system (@hotel-manager/ui): palette warm
 * boutique, ombre warm, font Inter. Coerente con KpiStrip/Layout reception.
 */
import { BedDouble, ChevronRight, Clock, Wallet } from '@hotel-manager/ui/icons';
import { Link } from 'react-router-dom';
import type { HotelToday } from '../../api/owner.js';

interface Props {
  hotel: HotelToday;
}

export function HotelTodayCard({ hotel }: Props) {
  const occupancyPct = hotel.roomsTotal
    ? Math.round((hotel.roomsOccupied / hotel.roomsTotal) * 100)
    : 0;

  return (
    <Link
      to={`/owner/hotels/${hotel.tenantId}`}
      data-testid="hotel-today-card"
      data-online={hotel.online}
      aria-label={`Gestisci ${hotel.name}`}
      className="group bg-bg-card rounded-2xl border border-border-base shadow-hm-sm p-5 flex flex-col gap-4 transition-all duration-150 hover:-translate-y-0.5 hover:shadow-hm-md hover:border-accent-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary focus-visible:ring-offset-2 focus-visible:ring-offset-bg-base"
    >
      {/* Header: nome + badge stato */}
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-lg font-bold text-text-primary leading-tight tracking-hm-tight truncate flex items-center gap-1">
            {hotel.name}
            <ChevronRight
              size={16}
              strokeWidth={2}
              className="text-text-muted opacity-0 -translate-x-1 transition-all group-hover:opacity-100 group-hover:translate-x-0 shrink-0"
            />
          </h2>
          <p className="text-xs text-text-muted mt-0.5 truncate">{hotel.slug}</p>
        </div>
        <StatusBadge online={hotel.online} everConnected={hotel.lastHeartbeatAt !== null} />
      </header>

      {/* Incasso del giorno — IN EVIDENZA */}
      <div className="rounded-xl bg-bg-muted/70 px-4 py-3.5 flex items-center gap-3">
        <span className="w-9 h-9 rounded-lg bg-accent-soft/60 flex items-center justify-center shrink-0">
          <Wallet size={18} strokeWidth={1.75} className="text-accent-primary-text" />
        </span>
        <div className="min-w-0">
          <div className="text-[11px] font-medium text-text-secondary uppercase tracking-[0.4px]">
            Incasso oggi
          </div>
          <div className="text-[28px] font-bold text-text-primary leading-none tracking-[-0.02em]">
            {formatEuro(hotel.todayRevenue)}
          </div>
        </div>
      </div>

      {/* Camere occupate / totali + barra occupazione */}
      <div>
        <div className="flex items-center justify-between gap-2 text-sm">
          <span className="flex items-center gap-1.5 text-text-secondary font-medium">
            <BedDouble size={15} strokeWidth={1.75} className="text-accent-primary" />
            Camere occupate
          </span>
          <span className="text-text-primary font-semibold">
            {hotel.roomsOccupied}
            <span className="text-text-muted font-normal">/{hotel.roomsTotal}</span>
            <span className="text-text-muted font-normal text-xs ml-1.5">({occupancyPct}%)</span>
          </span>
        </div>
        {/* Barra occupazione decorativa: il valore testuale e' gia' annunciato
            sopra (X/Y, Z%), quindi qui aria-hidden per non duplicare. */}
        <div
          aria-hidden="true"
          className="mt-2 h-1.5 w-full rounded-full bg-bg-muted overflow-hidden"
        >
          <div
            className="h-full rounded-full bg-accent-primary transition-[width] duration-300"
            style={{ width: `${occupancyPct}%` }}
          />
        </div>
      </div>

      {/* Ultimo contatto (heartbeat) in tempo relativo. Se l'hotel non si e'
          MAI collegato (lastHeartbeatAt null), "Mai collegato" neutro: dire
          "ultimo contatto: mai" suonerebbe come un guasto. */}
      <footer className="flex items-center gap-1.5 text-[11px] text-text-muted border-t border-border-base pt-3 mt-auto">
        <Clock size={13} strokeWidth={1.75} />
        {hotel.lastHeartbeatAt === null
          ? 'Mai collegato'
          : `ultimo contatto: ${formatRelative(hotel.lastHeartbeatAt)}`}
      </footer>
    </Link>
  );
}

function StatusBadge({ online, everConnected }: { online: boolean; everConnected: boolean }) {
  // "Mai collegato" (mai partito) e' uno stato NEUTRO, non un allarme:
  // stessa palette grigia dell'Offline, etichetta onesta.
  const label = online ? 'Online' : everConnected ? 'Offline' : 'Mai collegato';
  return (
    <span
      data-testid="status-badge"
      className={
        online
          ? 'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold bg-[#eef5e9] text-[#5F8A50] shrink-0'
          : 'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold bg-bg-muted text-text-muted shrink-0'
      }
    >
      <span
        aria-hidden="true"
        className={`inline-block w-2 h-2 rounded-full ${online ? 'bg-[#5F8A50]' : 'bg-text-muted'}`}
      />
      {label}
    </span>
  );
}

/** Formatta la stringa decimale euro ("1234.50") in "€ 1.234,50" (it-IT). */
function formatEuro(value: string): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return '€ —';
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
  }).format(n);
}

/** Tempo relativo in italiano da un ISO timestamp; null -> "mai". */
function formatRelative(iso: string | null, now: Date = new Date()): string {
  if (!iso) return 'mai';
  const then = new Date(iso);
  const ms = then.getTime();
  if (!Number.isFinite(ms)) return 'mai';
  const diffSec = Math.round((now.getTime() - ms) / 1000);
  if (diffSec < 0) return 'adesso';
  if (diffSec < 60) return 'adesso';
  const min = Math.floor(diffSec / 60);
  if (min < 60) return `${min} min fa`;
  const hours = Math.floor(min / 60);
  if (hours < 24) return `${hours} ${hours === 1 ? 'ora' : 'ore'} fa`;
  const days = Math.floor(hours / 24);
  return `${days} ${days === 1 ? 'giorno' : 'giorni'} fa`;
}
